import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '@/shared/lib/classnames/cn';
import { Loader } from './loader';
import type { VariantProps } from 'class-variance-authority';
const buttonVariants = cva('rounded-2xl inline-flex items-center justify-center whitespace-nowrap text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.95] hover:scale-x-[0.99] transition duration-300 ease-out touch-none relative', {
  variants: {
    variant: {
      'default': 'text-white bg-additionalBlue hover:bg-additionalBlue-dark focus-visible:ring-additionalBlue',
      'circle': 'rounded-full hover:scale-x-10',
      'secondary': 'bg-bgSecondary text-textPrimary disabled:opacity-80 hover:bg-bgSecondary-dark focus-visible:ring-bgSecondary touch-auto ',
      'secondary-error': 'bg-statusError/10 text-statusError focus-visible:ring-statusError',
      'secondary-main': 'bg-lightBlue text-statusInfo hover:bg-lightBlue-dark focus-visible:ring-lightBlue touch-auto overflow-auto',
      'outlined': 'bg-white text-additionalBlue border border-additionalBlue hover:text-additionalBlue-dark hover:border-additionalBlue-dark focus-visible:ring-primary',
      'text': 'p-0 text-sm text-additionalBlue active:text-additionalBlue/80 hover:underline active:transform-none hover:transform-none touch-auto overflow-auto',
      'glassmorphism': 'bg-bgPrimary/10 backdrop-saturate-2 backdrop-blur-3xl hover:bg-additionalBlue/10 active:bg-additionalBlue/20 touch-auto overflow-auto',
      'whatsapp': 'bg-lightGreen rounded-2xl hover:bg-lightGreen/20 hover:scale-x-100'
    },
    size: {
      xs: 'py-2',
      sm: 'py-3',
      default: 'py-4',
      lg: 'py-5',
      xl: 'py-6',
      icon: 'size-12 min-w-12'
    },
    textWeight: {
      default: 'font-medium',
      bold: 'font-bold',
      normal: 'font-normal'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
    textWeight: 'default'
  }
});
type ButtonVariants = VariantProps<typeof buttonVariants>;
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, ButtonVariants {
  asChild?: boolean;
  loading?: boolean;
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant,
  size,
  textWeight,
  asChild = false,
  loading = false,
  children,
  ...props
}, ref) => {
  const loaderColorMap = {
    'default': 'white',
    'secondary': 'secondary',
    'secondary-error': 'secondary-error',
    'secondary-main': 'secondary-main',
    'outlined': 'outlined'
  } as const;
  const getLoaderColor = (buttonVariant: ButtonProps['variant']) => {
    return loaderColorMap[buttonVariant as keyof typeof loaderColorMap] || 'primary';
  };
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({
    variant,
    size,
    textWeight,
    className
  }), loading ? 'pointer-events-none opacity-50' : '')} ref={ref} disabled={loading} {...props}>
        <>
          {children}
          {loading && <div className="absolute inset-0 flex items-center justify-center">
              <Loader className="size-6 animate-spin" size={'base'} color={getLoaderColor(variant)} />
            </div>}
        </>
      </Comp>;
});
Button.displayName = 'Button';
export { Button, buttonVariants, type ButtonProps, type ButtonVariants };